import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Plus, Trash, Tag, PencilSimple, DotsSixVertical } from "@phosphor-icons/react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const emptyForm = { name: "", type: "income", subs: "" };

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
      setCats(await api.listCategories());
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ name: c.name, type: c.type, subs: (c.subcategories || []).join(", ") });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    const payload = {
      name: form.name.trim(),
      type: form.type,
      subcategories: form.subs.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editingId) await api.updateCategory(editingId, payload);
      else await api.createCategory(payload);
      toast.success(editingId ? "Categoría actualizada" : "Categoría creada");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`¿Eliminar "${c.name}"? Las partidas ya registradas conservan su categoría.`)) return;
    try {
      await api.deleteCategory(c.id);
      toast.success("Categoría eliminada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleReorder = async (type, ids) => {
    const byId = Object.fromEntries(cats.map((c) => [c.id, c]));
    const others = cats.filter((c) => c.type !== type);
    setCats([...others, ...ids.map((id) => byId[id])]);
    try {
      await api.reorderCategories(ids);
    } catch (e) {
      toast.error("No se pudo guardar el orden");
      load();
    }
  };

  const incomes = cats.filter((c) => c.type === "income");
  const expenses = cats.filter((c) => c.type === "expense");

  return (
    <div>
      <div className="h-16 border-b border-border px-6 flex items-center justify-between bg-card">
        <div>
          <div className="label-xs text-muted-foreground">Taxonomía</div>
          <h1 className="font-heading text-xl font-extrabold tracking-tight">Categorías</h1>
        </div>
        <Button data-testid="new-category-btn" onClick={openCreate} className="rounded-sm font-semibold">
          <Plus size={16} className="mr-2" weight="bold" /> Nueva Categoría
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="category-dialog" className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-extrabold tracking-tight">
              {editingId ? "Editar Categoría" : "Nueva Categoría"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="label-xs text-muted-foreground">Nombre</Label>
              <Input data-testid="category-name-input" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="label-xs text-muted-foreground">Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="category-type-select" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Ingreso</SelectItem>
                  <SelectItem value="expense">Egreso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-xs text-muted-foreground">Subcategorías (separadas por coma)</Label>
              <Input data-testid="category-subs-input" value={form.subs}
                onChange={(e) => setForm({ ...form, subs: e.target.value })}
                placeholder="Sub1, Sub2, Sub3" className="mt-1 rounded-sm" />
            </div>
            {editingId && (
              <p className="text-xs text-muted-foreground">
                Los cambios aplican a nuevas partidas; las versiones ya guardadas no se modifican.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="confirm-category-btn" onClick={handleSave} className="rounded-sm font-semibold">
              {editingId ? "Guardar cambios" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CategorySection title="Ingresos" items={incomes} color="emerald" onEdit={openEdit} onDelete={handleDelete}
          onReorder={(ids) => handleReorder("income", ids)} />
        <CategorySection title="Egresos" items={expenses} color="red" onEdit={openEdit} onDelete={handleDelete}
          onReorder={(ids) => handleReorder("expense", ids)} />
      </div>
    </div>
  );
}

function CategorySection({ title, items, color, onEdit, onDelete, onReorder }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const ids = items.map((c) => c.id);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    onReorder(arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)));
  };

  return (
    <div className="card-elevated">
      <div className={`px-5 py-3 border-b border-border flex items-center justify-between ${color === "emerald" ? "bg-emerald-50 dark:bg-emerald-950" : "bg-red-50 dark:bg-red-950"}`}>
        <h3 className="font-heading font-bold">{title}</h3>
        <span className="label-xs text-muted-foreground">{items.length} · arrastra para ordenar</span>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="divide-y divide-border">
            {items.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">Vacío</div>}
            {items.map((c) => (
              <SortableCategoryRow key={c.id} c={c} onEdit={onEdit} onDelete={onDelete} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function SortableCategoryRow({ c, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: c.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style}
      className={`p-4 flex items-start justify-between gap-2 bg-card ${isDragging ? "relative z-10 shadow-lg ring-1 ring-foreground/20" : ""}`}
      data-testid={`category-${c.id}`}>
      <button {...attributes} {...listeners} data-testid={`drag-category-${c.id}`} aria-label="Arrastrar para ordenar"
        className="mt-0.5 p-1 -ml-1 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted cursor-grab active:cursor-grabbing touch-none">
        <DotsSixVertical size={16} weight="bold" />
      </button>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Tag size={14} weight="duotone" className="text-muted-foreground" />
                <span className="font-semibold">{c.name}</span>
                {c.is_default && <span className="label-xs text-muted-foreground">Predefinida</span>}
              </div>
              {c.subcategories?.length > 0 && (
                <div className="mt-2 flex items-center gap-1 flex-wrap">
                  {c.subcategories.map((s) => (
                    <span key={s} className="label-xs px-2 py-0.5 rounded-sm bg-muted">{s}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button data-testid={`edit-category-${c.id}`} onClick={() => onEdit(c)}
                className="p-1.5 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                <PencilSimple size={14} />
              </button>
              <button data-testid={`delete-category-${c.id}`} onClick={() => onDelete(c)}
                className="p-1.5 rounded-sm text-muted-foreground hover:text-destructive hover:bg-muted transition-colors">
              <Trash size={14} />
          </button>
        </div>
      </div>
  );
}
